import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  Zap,
  Brain,
  X,
  AlertTriangle,
  Clock,
  // Download,
  // Eye
} from 'lucide-react';
import { BatchProcessingDashboard } from './BatchProcessingDashboard';
import { dmsService } from '@/services/turfai/dmsService';
import { workflowService } from '@/services/turfai/workflowService';
import { authService } from '@/services/turfai/authService';
import { OnboardingSessionManager } from '@/utils/onboardingSession';

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  type: string;
  uploaded?: boolean;
  documentId?: string;
}

interface SmartDocumentCollectionProps {
  employeeData: {
    employee_id: string;
    full_name: string;
    department: string;
    start_date?: string;
    joiner_type?: string;
    experience_level?: string;
  };
  onComplete?: (results: any) => void;
  onShowTerms?: () => void;
}

enum ProcessingStage {
  COLLECTION = 'collection',
  UPLOAD = 'upload',
  PROCESSING = 'processing',
  RESULTS = 'results'
}

export const SmartDocumentCollection: React.FC<SmartDocumentCollectionProps> = ({
  employeeData,
  onComplete,
  onShowTerms
}) => {
  const [currentStage, setCurrentStage] = useState<ProcessingStage>(ProcessingStage.COLLECTION);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [collectionInfo, setCollectionInfo] = useState<{
    isExisting: boolean;
    existingDocCount?: number;
    message?: string;
  } | null>(null);

  // Check for existing session on mount - but always start fresh for testing
  useEffect(() => {
    // For testing, always clear any previous session and start fresh
    OnboardingSessionManager.clearSession();
    initializeNewSession();
    
    // Reset all state to ensure clean start
    setCurrentStage(ProcessingStage.COLLECTION);
    setUploadedFiles([]);
    setCollectionId(null);
    setExecutionId(null);
    setProgress(0);
    setError(null);
    setIsUploading(false);
    setShowResumePrompt(false);
    setCollectionInfo(null);
  }, [employeeData]);

  const initializeNewSession = () => {
    OnboardingSessionManager.createSession(employeeData.employee_id, employeeData);
  };

  const handleResumeSession = () => {
    const session = OnboardingSessionManager.getSession();
    if (session) {
      // Restore uploaded files info
      const restoredFiles = session.uploadedFiles.map((file: any) => ({
        id: file.id,
        file: new File([], file.fileName), // Placeholder, actual file needs re-upload if not completed
        preview: '',
        type: 'application/pdf',
        uploaded: file.status === 'uploaded' || file.status === 'completed',
        documentId: file.documentId
      }));
      setUploadedFiles(restoredFiles);
      OnboardingSessionManager.resumeSession();
    }
    setShowResumePrompt(false);
  };

  const handleStartFresh = () => {
    OnboardingSessionManager.clearSession();
    initializeNewSession();
    setShowResumePrompt(false);
    setCollectionId(null);
    setExecutionId(null);
    setUploadedFiles([]);
    setCurrentStage(ProcessingStage.COLLECTION);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    files.forEach(file => {
      const newFile: UploadedFile = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        type: file.type
      };
      
      setUploadedFiles(prev => [...prev, newFile]);
      
      // Save file info to session
      OnboardingSessionManager.addUploadedFile({
        id: newFile.id,
        fileName: file.name,
        status: 'pending'
      });
    });
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const createCollectionAndUpload = async () => {
    if (uploadedFiles.length === 0) {
      setError('Please upload at least one document');
      return;
    }

    setIsUploading(true);
    setError(null);
    
    try {
      // Step 0: Ensure we're authenticated
      if (!authService.isAuthenticated()) {
        console.log('🔐 Not authenticated, logging in with test credentials...');
        await authService.loginWithTestCredentials();
        console.log('✅ Authentication successful');
      }

      setCurrentStage(ProcessingStage.UPLOAD);
      setProgress(10);

      // Step 1: Find existing collection or create new
      let collection;
      // let isExistingCollection = false;
      
      if (collectionId) {
        // Use existing collection from resumed session
        console.log('📁 Using collection from session:', collectionId);
        collection = await dmsService.getCollection(collectionId);
        // isExistingCollection = true;
        
        // Get existing document count for user feedback
        try {
          const existingDocCount = await dmsService.getCollectionDocumentCount(collection.id);
          setCollectionInfo({
            isExisting: true,
            existingDocCount,
            message: existingDocCount > 0 
              ? `Resuming session - found ${existingDocCount} existing document(s). Adding new documents...`
              : 'Resuming session - adding your documents...'
          });
        } catch (err) {
          setCollectionInfo({
            isExisting: true,
            message: 'Resuming session - adding your documents...'
          });
        }
      } else {
        // Try to find existing collection for this employee
        console.log('🔍 Checking for existing collection for employee:', employeeData.employee_id);
        console.log('👤 Full employee data:', employeeData);
        const existingCollection = await dmsService.getCollectionByEmployeeId(employeeData.employee_id);
        
        if (existingCollection) {
          // Use existing collection
          console.log('✅ Found existing collection:', existingCollection.id);
          collection = existingCollection;
          setCollectionId(collection.id);
          OnboardingSessionManager.setCollectionId(collection.id);
          // isExistingCollection = true;
          
          // Get existing document count for user feedback
          try {
            const existingDocCount = await dmsService.getCollectionDocumentCount(collection.id);
            setCollectionInfo({
              isExisting: true,
              existingDocCount,
              message: existingDocCount > 0 
                ? `Found existing collection with ${existingDocCount} document(s). Adding your new documents...`
                : 'Found existing collection. Adding your documents...'
            });
          } catch (err) {
            setCollectionInfo({
              isExisting: true,
              message: 'Found existing collection. Adding your documents...'
            });
          }
        } else {
          // Create new collection only if none exists
          console.log('🆕 Creating new collection for employee:', employeeData.employee_id);
          collection = await dmsService.createOnboardingCollection(employeeData);
          setCollectionId(collection.id);
          OnboardingSessionManager.setCollectionId(collection.id);
          // isExistingCollection = false;
          
          setCollectionInfo({
            isExisting: false,
            message: 'Created new collection for your documents.'
          });
        }
      }
      setProgress(30);

      // Step 2: Upload all documents to collection
      const uploadErrors: string[] = [];
      const uploadPromises = uploadedFiles.map(async (fileData, index) => {
        try {
          const document = await dmsService.uploadDocument(
            collection.id,
            fileData.file,
            'unknown' // Will be classified automatically
          );
          
          // Update file status
          setUploadedFiles(prev => prev.map(f => 
            f.id === fileData.id 
              ? { ...f, uploaded: true, documentId: document.id }
              : f
          ));
          
          // Update session
          OnboardingSessionManager.addUploadedFile({
            id: fileData.id,
            fileName: fileData.file.name,
            documentId: document.id,
            status: 'uploaded'
          });
          
          setProgress(30 + (index + 1) / uploadedFiles.length * 30);
          return document;
        } catch (error) {
          const errorMsg = `Failed to upload ${fileData.file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          console.error(errorMsg, error);
          uploadErrors.push(errorMsg);
          
          // Mark file as failed
          setUploadedFiles(prev => prev.map(f => 
            f.id === fileData.id 
              ? { ...f, uploaded: false }
              : f
          ));
          
          return null; // Return null for failed uploads
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter((result: any) => result !== null);
      
      // Handle partial failures
      if (uploadErrors.length > 0) {
        if (successfulUploads.length === 0) {
          // All uploads failed
          throw new Error(`All document uploads failed:\n${uploadErrors.join('\n')}`);
        } else {
          // Some uploads failed - show warning but continue
          console.warn(`Some uploads failed: ${uploadErrors.join(', ')}`);
          setCollectionInfo({
            isExisting: true,
            message: `⚠️ ${successfulUploads.length}/${uploadedFiles.length} documents uploaded successfully. ${uploadErrors.length} failed.`
          });
        }
      }
      setProgress(70);

      // Step 3: Start intelligent batch processing
      if (successfulUploads.length === 0) {
        throw new Error('Cannot start workflow: No documents were uploaded successfully');
      }
      
      setCurrentStage(ProcessingStage.PROCESSING);
      
      try {
        console.log('🚀 Starting TurfAI batch workflow execution...');
        console.log(`📦 Current upload batch: ${successfulUploads.length} documents`);
        console.log('📄 Batch documents:', successfulUploads.map(doc => `${doc.attributes?.title || doc.title || doc.name} (${doc.id})`));
        
        const execution = await workflowService.executeBatchClassification(
          collection.id,
          employeeData, // Employee data for context/tracking only
          {
            parallel: true,
            maxConcurrent: 5,
            timeout: 300,
            retry: true,
            specificDocuments: successfulUploads.map(doc => ({
              id: doc.id,
              fileUrl: doc.attributes?.fileUrl || doc.fileUrl || doc.url,
              title: doc.attributes?.title || doc.title || doc.name,
              mimeType: doc.attributes?.mimeType || doc.mimeType,
              fileSize: doc.attributes?.fileSize || doc.fileSize
            }))
          }
        );
        
        if (!execution || !execution.id) {
          throw new Error('Workflow execution failed: No execution ID returned');
        }
        
        console.log('✅ Workflow execution started successfully:', execution.id);
        setExecutionId(execution.id);
        OnboardingSessionManager.setExecutionId(execution.id);
        setProgress(80);
        
        // Processing will continue in BatchProcessingDashboard
        
      } catch (workflowError) {
        console.error('Workflow execution failed:', workflowError);
        
        // Immediately reset stage when workflow execution fails
        setCurrentStage(ProcessingStage.UPLOAD);
        setProgress(0);
        
        // Provide specific error message for workflow failures
        let workflowErrorMsg = 'Failed to start AI processing';
        if (workflowError instanceof Error) {
          if (workflowError.message.includes('401')) {
            workflowErrorMsg = 'Authentication failed for AI processing. Please check your credentials.';
          } else if (workflowError.message.includes('404')) {
            workflowErrorMsg = 'AI processing service not found. Please ensure TurfAI services are running.';
          } else if (workflowError.message.includes('Invalid workflow inputs') || workflowError.message.includes('requires property')) {
            workflowErrorMsg = 'Workflow input format error. The document processing workflow expects a different input format. Please check TurfAI workflow configuration.';
          } else if (workflowError.message.includes('timeout')) {
            workflowErrorMsg = 'AI processing service is not responding. Please try again later.';
          } else {
            workflowErrorMsg = `AI processing failed: ${workflowError.message}`;
          }
        }
        
        throw new Error(workflowErrorMsg);
      }
      
    } catch (err) {
      console.error('Error in collection and upload:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to process documents';
      if (err instanceof Error) {
        if (err.message.includes('authentication') || err.message.includes('login')) {
          errorMessage = 'Authentication failed. Please check your TurfAI credentials and try the TurfAI Connection Test first.';
        } else if (err.message.includes('connection') || err.message.includes('fetch')) {
          errorMessage = 'Connection to TurfAI server failed. Please ensure TurfAI services are running and try the TurfAI Connection Test first.';
        } else if (err.message.includes('500') || err.message.includes('Server Error')) {
          errorMessage = 'TurfAI server encountered an internal error. Please check server logs and try again.';
        } else if (err.message.includes('Cannot start workflow')) {
          errorMessage = err.message; // Use the specific workflow error message
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      
      // Reset stage to show error instead of processing dashboard
      setCurrentStage(ProcessingStage.UPLOAD);
      setProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const onProcessingComplete = (results: any) => {
    setCurrentStage(ProcessingStage.RESULTS);
    setProgress(100);
    // Pass execution ID along with results
    onComplete?.({
      ...results,
      executionId: executionId
    });
  };

  const renderCollectionStage = () => (
    <div className="w-full max-w-4xl mx-auto p-6">
      {/* Resume Session Prompt */}
      {showResumePrompt && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Alert className="border-blue-200 bg-blue-50">
            <AlertTriangle className="h-5 w-5 text-blue-600" />
            <AlertDescription className="ml-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-blue-900 mb-1">
                    Resume Previous Session?
                  </p>
                  <p className="text-sm text-blue-700">
                    We found an incomplete document upload session. Would you like to continue where you left off?
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleStartFresh}
                    className="text-blue-700 border-blue-300 hover:bg-blue-100"
                  >
                    Start Fresh
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleResumeSession}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Resume Upload
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </motion.div>
      )}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl mx-auto flex items-center justify-center mb-6"
        >
          <Brain className="w-10 h-10 text-white" />
        </motion.div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Smart Document Processing
        </h1>
        <p className="text-gray-600 mb-6">
          Upload all your onboarding documents at once. Our AI will automatically classify, 
          extract information, and validate everything for you.
        </p>
        
        <div className="flex items-center justify-center gap-6 mb-8">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-500" />
            <span className="text-sm text-gray-600">Auto Classification</span>
          </div>
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-500" />
            <span className="text-sm text-gray-600">Smart Extraction</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm text-gray-600">Instant Validation</span>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="border-2 border-dashed border-purple-200 rounded-lg p-8 text-center hover:border-purple-300 transition-colors">
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileUpload}
              className="hidden"
              id="document-upload"
            />
            <label 
              htmlFor="document-upload" 
              className="cursor-pointer flex flex-col items-center gap-4"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Upload className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Upload Your Documents
                </p>
                <p className="text-sm text-gray-600">
                  Drag and drop files here, or click to browse
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Supports: PDF, JPG, PNG, DOC, DOCX (Max 10MB each)
                </p>
              </div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Uploaded Documents ({uploadedFiles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{file.file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {file.uploaded && (
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Uploaded
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(file.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Collection Info Display */}
      {collectionInfo && (
        <Alert 
          variant={collectionInfo.isExisting ? "default" : "default"} 
          className="mb-6 border-blue-200 bg-blue-50"
        >
          <CheckCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            {collectionInfo.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Action Button */}
      <div className="text-center">
        <Button
          onClick={createCollectionAndUpload}
          disabled={uploadedFiles.length === 0 || isUploading}
          size="lg"
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
        >
          {isUploading ? (
            <>
              <Clock className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Brain className="w-4 h-4 mr-2" />
              Start Smart Processing
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderUploadStage = () => (
    <div className="w-full max-w-2xl mx-auto p-6 text-center">
      {/* Show error if there is one */}
      {error ? (
        <div>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-16 h-16 bg-red-100 rounded-2xl mx-auto flex items-center justify-center mb-6"
          >
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Processing Failed</h2>
          
          <Alert variant="destructive" className="mb-6 text-left">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          
          <Button
            onClick={() => {
              setError(null);
              setProgress(0);
              setCurrentStage(ProcessingStage.COLLECTION);
            }}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            Try Again
          </Button>
        </div>
      ) : (
        <div>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-16 h-16 bg-blue-100 rounded-2xl mx-auto flex items-center justify-center mb-6"
          >
            <Upload className="w-8 h-8 text-blue-600 animate-bounce" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Uploading Documents</h2>
          <p className="text-gray-600 mb-6">
            Uploading your documents to secure storage...
          </p>
          
          <Progress value={progress} className="mb-4" />
          <p className="text-sm text-gray-500">{Math.round(progress)}% Complete</p>
        </div>
      )}
    </div>  
  );

  const renderProcessingStage = () => (
    <div className="w-full">
      {executionId ? (
        <BatchProcessingDashboard
          executionId={executionId}
          onComplete={onProcessingComplete}
        />
      ) : (
        <div className="text-center p-6">
          <Clock className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p>Initializing processing...</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <header className="border-b border-gray-200/50 bg-white/50 backdrop-blur-sm p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/openturf-logo.svg" 
              alt="OpenTurf" 
              className="h-8 w-auto"
            />
            <div className="text-sm text-gray-600">
              Employee Onboarding • {employeeData.full_name}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              Stage: <span className="capitalize font-medium">{currentStage}</span>
            </div>
            <Progress value={progress} className="w-32 h-2" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {currentStage === ProcessingStage.COLLECTION && renderCollectionStage()}
            {currentStage === ProcessingStage.UPLOAD && renderUploadStage()}
            {currentStage === ProcessingStage.PROCESSING && renderProcessingStage()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200/50 bg-white/50 backdrop-blur-sm p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>© {new Date().getFullYear()} OpenTurf Technologies</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Powered by TurfAI Intelligence</span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};